import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { diretoriaToneTextClass } from '@/lib/diretoriaKpiTone';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useRiqueChatStore } from '@/stores/riqueChatStore';
import { RiqueIcon } from '@/components/Rique/RiqueIcon';
import { DIRETORIA_MODULE_TITLE } from '@/data/diretoriaConstants';
import {
  buildExecutiveCards,
  dashboardToAgingChart,
  dashboardToEmpresaBar,
  type DiretoriaDashboardStats,
  type DiretoriaFaturamentoMesStats,
  type DiretoriaReceitaProjetadaStats,
} from '@/lib/diretoriaDashboard';
import { getPlaceholderIndicators } from '@/data/diretoriaIndicatorsCatalog';
import { EMPTY_AGING_CHART, EMPTY_EVOLUCAO_CHART, type DiretoriaIndicator } from '@/data/diretoriaTypes';
import { DiretoriaModalChart } from '@/components/Diretoria/DiretoriaModalChart';
import { DiretoriaExecutiveStrip } from '@/components/Diretoria/DiretoriaExecutiveStrip';
import { DiretoriaOverviewSection } from '@/components/Diretoria/DiretoriaOverviewSection';
import { DiretoriaOficinaSection } from '@/components/Diretoria/DiretoriaOficinaSection';
import { DiretoriaSparkline } from '@/components/Diretoria/DiretoriaSparkline';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';
import { useDiretoriaSqlCatalog } from '@/hooks/useDiretoriaSqlCatalog';
import {
  buildCustoMotoIndicatorFromErp,
  type DiretoriaCustoPorMotoPayload,
} from '@/lib/diretoriaCustoMotoIndicator';
import {
  buildFaturamentoBrutoIndicatorFromErp,
  buildInadimplenciaIndicatorFromErp,
  buildInfracoesIndicatorFromErp,
  buildLocacaoLocalIndicatorFromErp,
  buildMotosAlugadasIndicatorFromErp,
  buildRentabilidadeIndicatorFromErp,
  type DiretoriaFaturamentoBrutoPayload,
  type DiretoriaInadimplenciaPayload,
  type DiretoriaInfracoesPayload,
  type DiretoriaLocacaoLocalPayload,
  type DiretoriaMotosAlugadasPayload,
  type DiretoriaRentabilidadePayload,
} from '@/lib/diretoriaErpIndicatorBuilders';
import {
  DIRETORIA_EXECUTIVE_QUERY_IDS,
  DIRETORIA_OFICINA_QUERY_IDS,
  DIRETORIA_OVERVIEW_QUERY_IDS,
  getIndicatorQueryId,
} from '@/lib/diretoriaQueryIds';
import { fetchDiretoriaErp, parseCompanyCoderp, probeDiretoriaErp } from '@/lib/diretoriaErpFetch';
import {
  fetchDiretoriaOficinaProdutividade,
  formatDateISO,
  startOfMonthISO,
} from '@/lib/diretoriaOficinaFetch';

interface DiretoriaErpSnapshotData {
  configured: boolean;
  fetchedAt: string;
  ping?: unknown;
  upstreamError?: string;
}

interface DiretoriaErpSnapshotPayload {
  success: boolean;
  data?: DiretoriaErpSnapshotData;
  message?: string;
}

interface DiretoriaMotosDisponiveisMarca {
  idEmpresa: number;
  nomeEmpresa: string;
  marca: string;
  quantidadeMotos: number;
}

interface DiretoriaMotosDisponiveisPayload {
  totalDisponiveis: number;
  marcas: DiretoriaMotosDisponiveisMarca[];
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

interface DiretoriaMotosPorStatusItem {
  idEmpresa: number;
  statusEquipamento: string;
  quantidadeMotos: number;
}

interface DiretoriaMotosPorStatusPayload {
  totalMotos: number;
  status: DiretoriaMotosPorStatusItem[];
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

interface DiretoriaCustoPorMotoPayloadExtended extends DiretoriaCustoPorMotoPayload {
  companyId?: string;
  empresaid?: number;
}

interface DiretoriaCompanyOption {
  id: string;
  name: string;
  coderp?: string | null;
}

const formatCurrencyPtBr = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export const DiretoriaPage: React.FC = () => {
  const { user } = useAuthStore();
  const openRique = useRiqueChatStore((s) => s.openRique);
  const [companyId, setCompanyId] = useState<string>('');
  const [open, setOpen] = useState<DiretoriaIndicator | null>(null);
  const [oficinaDataFrom, setOficinaDataFrom] = useState(() => startOfMonthISO());
  const [oficinaDataTo, setOficinaDataTo] = useState(() => formatDateISO(new Date()));

  const { data: companies } = useQuery({
    queryKey: ['diretoria-companies', user?.id, user?.role],
    queryFn: async () => {
      const response = await api.get('/companies?page=1&limit=500');
      const payload = response?.data?.data ?? response?.data ?? [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.companies)) return payload.companies;
      if (Array.isArray(payload?.items)) return payload.items;
      return [];
    },
  });

  const { data: erpSnapshotPayload } = useQuery({
    queryKey: ['diretoria-erp-snapshot'],
    queryFn: async (): Promise<DiretoriaErpSnapshotPayload> => {
      try {
        const response = await api.get<DiretoriaErpSnapshotPayload>('/reports/diretoria/erp-snapshot');
        return response.data;
      } catch (e: unknown) {
        if (isAxiosError(e) && e.response?.status === 503) {
          return { success: true, data: { configured: false, fetchedAt: new Date().toISOString() } };
        }
        if (isAxiosError(e) && e.response?.status === 502) {
          const body = e.response.data as DiretoriaErpSnapshotPayload & { data?: DiretoriaErpSnapshotData };
          return {
            success: false,
            message: typeof body?.message === 'string' ? body.message : 'ERP indisponível.',
            data:
              body?.data ?? {
                configured: true,
                fetchedAt: new Date().toISOString(),
                upstreamError: 'Falha ao consultar o ERP FlyStart.',
              },
          };
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });

  const {
    data: dashboardStats,
    isLoading: dashboardLoading,
    isError: dashboardError,
  } = useQuery({
    queryKey: ['diretoria-dashboard', companyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (companyId) params.append('companyId', companyId);
      const response = await api.get(`/reports/dashboard?${params.toString()}`);
      return response.data.data as DiretoriaDashboardStats;
    },
    retry: 1,
    staleTime: 30_000,
  });

  const {
    data: oficinaProdutividade,
    isLoading: oficinaLoading,
  } = useQuery({
    queryKey: ['diretoria-oficina', companyId, oficinaDataFrom, oficinaDataTo],
    queryFn: () => fetchDiretoriaOficinaProdutividade(companyId, oficinaDataFrom, oficinaDataTo),
    staleTime: 60_000,
  });

  const { getSql, getSource } = useDiretoriaSqlCatalog(companyId);

  const { data: faturamentoMes } = useQuery({
    queryKey: ['diretoria-faturamento-mes', companyId],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaFaturamentoMesStats & { querySql?: string }>(
        '/reports/diretoria/faturamento-mes',
        companyId
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: receitaProjetada } = useQuery({
    queryKey: ['diretoria-receita-projetada', companyId],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaReceitaProjetadaStats & { querySql?: string }>(
        '/reports/diretoria/receita-projetada',
        companyId
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: motosDisponiveis } = useQuery({
    queryKey: ['diretoria-motos-disponiveis', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaMotosDisponiveisPayload>('/reports/diretoria/motos-disponiveis', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: motosPorStatus } = useQuery({
    queryKey: ['diretoria-motos-por-status', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaMotosPorStatusPayload>('/reports/diretoria/motos-por-status', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: custoPorMoto } = useQuery({
    queryKey: ['diretoria-custo-por-moto', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaCustoPorMotoPayloadExtended>('/reports/diretoria/custo-por-moto', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: evolucaoErp } = useQuery({
    queryKey: ['diretoria-evolucao-receita', companyId],
    queryFn: () =>
      fetchDiretoriaErp<{ ano: number; series: Array<{ name: string; valor: number; valor2: number }>; querySql?: string }>(
        '/reports/diretoria/evolucao-receita-projetada',
        companyId
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: faturamentoBruto } = useQuery({
    queryKey: ['diretoria-faturamento-bruto', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaFaturamentoBrutoPayload>('/reports/diretoria/faturamento-bruto', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: inadimplenciaErp } = useQuery({
    queryKey: ['diretoria-inadimplencia', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaInadimplenciaPayload>('/reports/diretoria/inadimplencia', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: infracoesErp } = useQuery({
    queryKey: ['diretoria-infracoes', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaInfracoesPayload>('/reports/diretoria/infracoes', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: motosAlugadas } = useQuery({
    queryKey: ['diretoria-motos-alugadas', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaMotosAlugadasPayload>('/reports/diretoria/motos-alugadas', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: rentabilidadeMoto } = useQuery({
    queryKey: ['diretoria-rentabilidade-moto', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaRentabilidadePayload>('/reports/diretoria/rentabilidade-moto', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: locacaoLocal } = useQuery({
    queryKey: ['diretoria-locacao-local', companyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaLocacaoLocalPayload>('/reports/diretoria/locacao-local', companyId),
    retry: 1,
    staleTime: 60_000,
  });

  const companyList = useMemo(
    () => (Array.isArray(companies) ? (companies as DiretoriaCompanyOption[]) : []),
    [companies]
  );

  const selectedCompany = useMemo(
    () => (companyId ? companyList.find((c) => c.id === companyId) : undefined),
    [companyId, companyList]
  );

  const missingCoderp = Boolean(companyId && selectedCompany && parseCompanyCoderp(selectedCompany.coderp) == null);

  const { data: erpProbe } = useQuery({
    queryKey: ['diretoria-erp-probe', companyId],
    queryFn: () => probeDiretoriaErp(companyId),
    enabled: erpSnapshotPayload?.data?.configured !== false && !missingCoderp,
    retry: false,
    staleTime: 30_000,
  });

  const companyLabel = useMemo(() => {
    if (!companyId) return 'Todas as empresas';
    return selectedCompany?.name ?? companyId;
  }, [companyId, selectedCompany]);

  const baseIndicators = useMemo(() => getPlaceholderIndicators(), []);

  const indicators = useMemo(() => {
    return baseIndicators.map((indicator) => {
      if (indicator.id === 'custo-moto' && custoPorMoto) {
        return buildCustoMotoIndicatorFromErp(indicator, custoPorMoto, formatCurrencyPtBr);
      }
      if (indicator.id === 'faturamento-bruto' && faturamentoBruto) {
        return buildFaturamentoBrutoIndicatorFromErp(indicator, faturamentoBruto, formatCurrencyPtBr);
      }
      if (indicator.id === 'inadimplencia' && inadimplenciaErp) {
        return buildInadimplenciaIndicatorFromErp(indicator, inadimplenciaErp, formatCurrencyPtBr);
      }
      if (indicator.id === 'infracoes' && infracoesErp) {
        return buildInfracoesIndicatorFromErp(indicator, infracoesErp, formatCurrencyPtBr);
      }
      if (indicator.id === 'motos-alugadas' && motosAlugadas) {
        return buildMotosAlugadasIndicatorFromErp(indicator, motosAlugadas);
      }
      if (indicator.id === 'rentabilidade-moto' && rentabilidadeMoto) {
        return buildRentabilidadeIndicatorFromErp(indicator, rentabilidadeMoto, formatCurrencyPtBr);
      }
      if (indicator.id === 'locacao-local' && locacaoLocal) {
        return buildLocacaoLocalIndicatorFromErp(indicator, locacaoLocal);
      }
      if (indicator.id === 'motos-status' && motosPorStatus) {
        const statusMap = new Map<string, number>();
        for (const row of motosPorStatus.status ?? []) {
          const key = row.statusEquipamento || 'Sem status';
          statusMap.set(key, (statusMap.get(key) ?? 0) + row.quantidadeMotos);
        }
        const statusRows = Array.from(statusMap.entries())
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value);
        const topStatus = statusRows[0];
        const criticalStatus = statusRows
          .filter((row) => /apropr|furto|roubo|apreend/i.test(row.name))
          .reduce((sum, row) => sum + row.value, 0);
        return {
          ...indicator,
          shortDescription: 'Distribuição atual da frota por status operacional (ERP FlyStart).',
          kpis: [
            { label: 'Total na base', value: String(motosPorStatus.totalMotos), hint: 'ERP FlyStart', tone: 'neutral' },
            {
              label: 'Maior status',
              value: topStatus
                ? `${topStatus.name} (${motosPorStatus.totalMotos ? ((topStatus.value / motosPorStatus.totalMotos) * 100).toFixed(1) : '0'}%)`
                : '—',
              hint: 'ERP FlyStart',
              tone: /apropr|furto|roubo|apreend/i.test(topStatus?.name ?? '') ? 'bad' : 'neutral',
            },
            {
              label: 'Situações críticas',
              value: String(criticalStatus),
              hint: 'Apropriação + Furto/Roubo + Apreendida',
              tone: criticalStatus === 0 ? 'good' : 'bad',
            },
          ],
          chartKind: 'pie',
          pieData: statusRows,
          tableHeaders: ['Status', 'Qtd.', '% frota'],
          tableRows: statusRows.map((row) => [
            row.name,
            String(row.value),
            motosPorStatus.totalMotos ? `${((row.value / motosPorStatus.totalMotos) * 100).toFixed(1)}%` : '0%',
          ]),
          footnote: 'ERP FlyStart: tipo=moto, ativo=1, deleted_at IS NULL.',
          querySql: motosPorStatus.querySql,
          dataSource: 'erp',
          hasData: true,
        } satisfies DiretoriaIndicator;
      }
      if (indicator.id === 'motos-disponiveis' && motosDisponiveis) {
        const marcas = [...(motosDisponiveis.marcas ?? [])].sort((a, b) => b.quantidadeMotos - a.quantidadeMotos);
        const topMarca = marcas[0];
        return {
          ...indicator,
          shortDescription: 'Motos disponíveis por marca (ERP FlyStart).',
          kpis: [
            {
              label: 'Disponíveis hoje',
              value: String(motosDisponiveis.totalDisponiveis),
              hint: 'ERP FlyStart',
              tone: motosDisponiveis.totalDisponiveis > 0 ? 'good' : 'neutral',
            },
            {
              label: 'Top marca',
              value: topMarca ? `${topMarca.marca} (${topMarca.quantidadeMotos})` : '—',
              hint: 'ERP FlyStart',
              tone: 'neutral',
            },
            {
              label: 'Marcas',
              value: String(new Set(marcas.map((row) => row.marca)).size),
              hint: 'ERP FlyStart',
              tone: 'neutral',
            },
          ],
          chartKind: 'bar',
          series: marcas.map((row) => ({ name: row.marca, valor: row.quantidadeMotos })),
          barSeriesKey: 'valor',
          tableHeaders: ['Empresa', 'Marca', 'Disponíveis'],
          tableRows: marcas.map((row) => [row.nomeEmpresa, row.marca, String(row.quantidadeMotos)]),
          footnote: 'ERP FlyStart: status_equipamento=disponivel, ativo=1.',
          querySql: motosDisponiveis.querySql,
          dataSource: 'erp',
          hasData: true,
        } satisfies DiretoriaIndicator;
      }
      return indicator;
    });
  }, [
    baseIndicators,
    custoPorMoto,
    faturamentoBruto,
    inadimplenciaErp,
    infracoesErp,
    motosAlugadas,
    rentabilidadeMoto,
    locacaoLocal,
    motosDisponiveis,
    motosPorStatus,
  ]);

  const dashboardReady = Boolean(dashboardStats) && !dashboardError && !dashboardLoading;

  const executiveCards = useMemo(
    () => buildExecutiveCards(dashboardStats, faturamentoMes, receitaProjetada),
    [dashboardStats, faturamentoMes, receitaProjetada]
  );

  const evolucao = evolucaoErp?.series?.length ? evolucaoErp.series : EMPTY_EVOLUCAO_CHART;

  const evolucaoSubtitle = evolucaoErp?.series?.length
    ? `Receita realizada (baixas) vs projetado (vencimentos) — ${evolucaoErp.ano} · ERP FlyStart`
    : 'Sem dados do ERP para o filtro selecionado';

  const agingChart = dashboardReady ? (dashboardToAgingChart(dashboardStats) ?? EMPTY_AGING_CHART) : EMPTY_AGING_CHART;

  const empresasChart = dashboardReady ? dashboardToEmpresaBar(dashboardStats, 8) : [];

  const agingSubtitle = dashboardReady
    ? 'Saldo em R$ e quantidade de títulos (cobrança)'
    : dashboardLoading
      ? 'Carregando dados de cobrança…'
      : 'Sem dados de cobrança para o filtro selecionado';

  const empresasSubtitle = dashboardReady
    ? 'Saldo em R$ por empresa (cobrança)'
    : dashboardLoading
      ? 'Carregando dados de cobrança…'
      : 'Sem dados de cobrança para o filtro selecionado';

  const erp = erpSnapshotPayload;
  const erpOk =
    Boolean(erp?.success && erp.data?.configured && erp.data.ping != null && !erp.data.upstreamError);
  const erpDegraded = Boolean(erp && erp.success === false && erp.data?.configured);
  const erpNotConfigured = Boolean(erp?.data && !erp.data.configured);

  const resolveExecutiveSql = (cardId: string) => {
    const queryId = DIRETORIA_EXECUTIVE_QUERY_IDS[cardId];
    if (!queryId) return {};
    const card = executiveCards.find((c) => c.id === cardId);
    const override =
      cardId === 'exec-faturamento-mes'
        ? faturamentoMes?.querySql
        : cardId === 'exec-receita-projetada'
          ? receitaProjetada?.querySql
          : undefined;
    return { sql: getSql(queryId, override), source: getSource(queryId), title: card?.label };
  };

  const overviewTitles: Record<'evolucao' | 'aging' | 'empresas', string> = {
    evolucao: 'Evolução — receita vs projetado',
    aging: 'Títulos a receber vencidos por faixa',
    empresas: 'Valor por empresa (R$)',
  };

  const resolveOverviewSql = (chartKey: 'evolucao' | 'aging' | 'empresas') => {
    const queryId = DIRETORIA_OVERVIEW_QUERY_IDS[chartKey];
    const override = chartKey === 'evolucao' ? evolucaoErp?.querySql : undefined;
    return {
      sql: getSql(queryId, override),
      source: getSource(queryId),
      title: overviewTitles[chartKey],
    };
  };

  const resolveIndicatorSql = (ind: DiretoriaIndicator) => {
    const queryId = getIndicatorQueryId(ind.id);
    return {
      sql: getSql(queryId, ind.querySql),
      source:
        ind.dataSource === 'cobranca'
          ? ('cobranca' as const)
          : ind.dataSource === 'oficina'
            ? ('oficina' as const)
            : getSource(queryId),
      title: ind.title,
    };
  };

  const oficinaSqlTitles: Record<string, string> = {
    [DIRETORIA_OFICINA_QUERY_IDS.porTecnico]: 'Produtividade por técnico',
    [DIRETORIA_OFICINA_QUERY_IDS.locacaoEmpresa]: 'Serviços por status de locação — por empresa',
    [DIRETORIA_OFICINA_QUERY_IDS.agendamentos]: 'KPIs de agendamentos',
    [DIRETORIA_OFICINA_QUERY_IDS.osPeriodo]: 'OS realizadas no período',
  };

  const resolveOficinaSql = (queryId: string) => ({
    sql: getSql(queryId),
    source: getSource(queryId) ?? ('oficina' as const),
    title: oficinaSqlTitles[queryId] ?? queryId,
  });

  return (
    <>
      <Helmet>
        <title>{DIRETORIA_MODULE_TITLE} — PortalFly</title>
      </Helmet>

      <div className="mx-auto w-full max-w-[min(100%,1680px)] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-muted-foreground">
              <BarChart3 className="h-5 w-5" aria-hidden />
              <span className="text-xs font-medium uppercase tracking-wide">Módulo</span>
            </div>
            <h1 className="font-heading text-heading-2 font-bold text-foreground">{DIRETORIA_MODULE_TITLE}</h1>
          </div>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Empresa
            <select
              className={cn(
                'h-9 min-w-[14rem] rounded-md border border-input bg-background px-2 text-sm text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              <option value="">Todas as empresas</option>
              {companyList.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                  {parseCompanyCoderp(company.coderp) == null ? ' (sem Código ERP)' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="text-xs text-muted-foreground">
          Filtro ativo: <span className="font-medium text-foreground">{companyLabel}</span>
          {' · '}
          Indicadores ERP (FlyStart) e cobrança (MySQL) — sem dados fictícios.
        </p>

        {erpOk ? (
          <div
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200"
            role="status"
          >
            ERP FlyStart: conexão ativa. Última verificação:{' '}
            <span className="tabular-nums">{new Date(erp!.data!.fetchedAt).toLocaleString('pt-BR')}</span>.
          </div>
        ) : null}
        {erpNotConfigured ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            ERP FlyStart não configurado. Indicadores de frota/financeiro ERP exibem &quot;—&quot; até a integração SQL
            ser habilitada.
          </div>
        ) : null}
        {erpDegraded ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            ERP FlyStart indisponível ({erp?.message ?? erp?.data?.upstreamError ?? 'erro'}). Indicadores ERP
            permanecem vazios até a conexão voltar.
          </div>
        ) : null}
        {missingCoderp ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            A empresa <span className="font-medium">{companyLabel}</span> não possui{' '}
            <span className="font-medium">Código ERP</span> (empresaid FlyStart) configurado. Preencha em{' '}
            <Link to="/companies" className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-50">
              Empresas
            </Link>{' '}
            → editar → campo &quot;Código ERP&quot;. Enquanto isso, os indicadores ERP exibem &quot;—&quot; para este
            filtro (o ping de conexão pode estar OK mesmo sem código de empresa).
          </div>
        ) : null}
        {!missingCoderp && erpProbe && !erpProbe.ok && erpOk ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            Conexão ERP ativa, mas a consulta de indicadores falhou
            {erpProbe.error.status ? ` (HTTP ${erpProbe.error.status})` : ''}:{' '}
            <span className="font-medium">{erpProbe.error.message}</span>.
            {companyId ? (
              <>
                {' '}
                Confira o Código ERP em{' '}
                <Link
                  to="/companies"
                  className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-50"
                >
                  Empresas
                </Link>{' '}
                ou teste o filtro &quot;Todas as empresas&quot;.
              </>
            ) : null}
          </div>
        ) : null}

        <DiretoriaExecutiveStrip cards={executiveCards} resolveSql={resolveExecutiveSql} />

        <DiretoriaOficinaSection
          data={oficinaProdutividade}
          isLoading={oficinaLoading}
          companyLabel={companyLabel}
          resolveSql={resolveOficinaSql}
          dataFrom={oficinaDataFrom}
          dataTo={oficinaDataTo}
          onPeriodChange={(from, to) => {
            setOficinaDataFrom(from);
            setOficinaDataTo(to);
          }}
        />

        <DiretoriaOverviewSection
          evolucao={evolucao}
          aging={agingChart}
          empresas={empresasChart}
          evolucaoSubtitle={evolucaoSubtitle}
          agingSubtitle={agingSubtitle}
          empresasSubtitle={empresasSubtitle}
          resolveSql={resolveOverviewSql}
        />

        <div>
          <h2 className="mb-3 font-heading text-lg font-semibold text-foreground">Indicadores</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {indicators.map((ind) => {
              const pk = ind.primaryKpiIndex ?? 0;
              const primary = ind.kpis[pk];
              const secondary = ind.kpis.filter((_, i) => i !== pk);
              return (
                <Card key={ind.id} className="relative h-full border-border/80 hover:shadow-card-hover">
                  <div className="absolute right-2 top-2 z-10">
                    <DiretoriaSqlButton {...resolveIndicatorSql(ind)} stopPropagation />
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(ind)}
                    className="w-full text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <CardHeader className="pb-2 pr-10">
                      <CardTitle className="text-base leading-snug">{ind.title}</CardTitle>
                      <p className="text-xs text-muted-foreground line-clamp-2">{ind.shortDescription}</p>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-0">
                      {primary ? (
                        <div>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {primary.label}
                          </p>
                          <p
                            className={cn(
                              'font-heading text-2xl font-bold tabular-nums',
                              diretoriaToneTextClass(primary.tone)
                            )}
                          >
                            {primary.value}
                          </p>
                        </div>
                      ) : null}
                      <DiretoriaSparkline indicator={ind} />
                      {secondary.map((k) => (
                        <div
                          key={k.label}
                          className="flex items-baseline justify-between gap-2 border-t border-border/60 pt-2 first:border-t-0 first:pt-0"
                        >
                          <span className="text-xs text-muted-foreground">{k.label}</span>
                          <span className={cn('text-sm font-semibold', diretoriaToneTextClass(k.tone))}>{k.value}</span>
                        </div>
                      ))}
                      <span className="inline-block pt-1 text-xs font-medium text-primary">Ver análise</span>
                    </CardContent>
                  </button>
                </Card>
              );
            })}
            <Card
              className={cn(
                'relative flex h-full flex-col overflow-hidden border-border/80 border-dashed bg-gradient-to-br from-primary/[0.07] via-background to-muted/50',
                'shadow-soft-sm transition-shadow duration-200 hover:shadow-card-hover'
              )}
            >
              <div
                className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary/[0.12] blur-2xl"
                aria-hidden
              />
              <CardHeader className="relative space-y-0 pb-3 pt-1">
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white p-1 shadow-inner ring-1 ring-border/40"
                    aria-hidden
                  >
                    <RiqueIcon className="h-full w-full" />
                  </div>
                  <div className="min-w-0 space-y-0.5 pt-0.5">
                    <CardTitle className="text-base leading-tight">Rique.Ia</CardTitle>
                    <p className="text-xs font-medium text-muted-foreground">Assistente CorpsTek</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative flex flex-1 flex-col gap-4 pt-0">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Dúvidas? Entre em contato com o seu assistente,{' '}
                  <span className="font-semibold text-foreground">Rique.Ia</span>.
                </p>
                <div className="mt-auto border-t border-border/60 pt-3">
                  <button
                    type="button"
                    onClick={() =>
                      openRique({
                        prompt: `Estou na ${DIRETORIA_MODULE_TITLE}. Resuma os indicadores mais relevantes e responda dúvidas sobre os dados exibidos.`,
                      })
                    }
                    className={cn(
                      'inline-flex h-9 w-full items-center justify-center gap-2 rounded-button px-4 text-sm font-medium',
                      'bg-primary text-primary-foreground shadow-button',
                      'hover:bg-primary/90 hover:shadow-button-hover hover:-translate-y-0.5',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'sm:w-auto sm:justify-start'
                    )}
                  >
                    Abrir chat
                    <RiqueIcon className="h-4 w-4 shrink-0 rounded-full bg-white/95 p-0.5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Modal isOpen={Boolean(open)} onClose={() => setOpen(null)} title={open?.title ?? 'Indicador'} size="xl">
        {open ? (
          <div className="space-y-4">
            <div className="flex items-center justify-end">
              <DiretoriaSqlButton {...resolveIndicatorSql(open)} />
            </div>
            {!open.hasData ? (
              <p className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
                Nenhum dado disponível para este indicador com o filtro atual. Verifique a conexão ERP ou selecione
                outra empresa.
              </p>
            ) : (
              <>
                <DiretoriaModalChart indicator={open} height={open.chartKind === 'pie' ? 320 : 260} />
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50 text-left">
                        {open.tableHeaders.map((h) => (
                          <th key={h} className="px-3 py-2 font-medium">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {open.tableRows.length === 0 ? (
                        <tr>
                          <td colSpan={open.tableHeaders.length} className="px-3 py-6 text-center text-muted-foreground">
                            Sem registros
                          </td>
                        </tr>
                      ) : (
                        open.tableRows.map((row, ri) => (
                          <tr key={ri} className="border-b border-border/80 last:border-0">
                            {row.map((cell, ci) => (
                              <td key={ci} className="px-3 py-2 text-foreground">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <p className="text-xs leading-relaxed text-muted-foreground">{open.footnote}</p>
            {open.id === 'ia-zeh' ? (
              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                <Link
                  to="/whatsapp-integration"
                  className={cn(
                    'inline-flex h-8 items-center justify-center rounded-button border border-input bg-background px-3 text-body-sm font-medium shadow-soft-sm',
                    'hover:bg-muted hover:border-input hover:shadow-soft-md'
                  )}
                >
                  Abrir integrações WhatsApp
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};
